import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { ADMIN, AUTH } from "@/constants/testIds";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LogOut, Trash2, RefreshCw, Calendar, Phone, Mail, MapPin, Crown, Camera } from "lucide-react";
import { PACKAGES, ADDONS, BIN_FREQUENCIES } from "@/data/content";
import { toast } from "sonner";
import Logo from "@/components/Logo";

const STATUSES = ["pending", "confirmed", "completed", "cancelled"];

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const nav = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user || user.role !== "admin") {
      nav("/admin/login");
    }
  }, [user, loading, nav]);

  const fetchAll = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/bookings");
      setBookings(data);
    } catch {
      toast.error("Failed to load bookings.");
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    if (user && user.role === "admin") fetchAll();
  }, [user, fetchAll]);

  const updateStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`/bookings/${id}/status`, { status });
      setBookings((b) => b.map((x) => (x.id === id ? data : x)));
      toast.success(`Marked ${status}`);
    } catch {
      toast.error("Update failed.");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this booking?")) return;
    try {
      await api.delete(`/bookings/${id}`);
      setBookings((b) => b.filter((x) => x.id !== id));
      toast.success("Deleted");
    } catch {
      toast.error("Delete failed.");
    }
  };

  const stats = {
    total: bookings.length,
    pending: bookings.filter((b) => b.status === "pending").length,
    confirmed: bookings.filter((b) => b.status === "confirmed").length,
    revenue: bookings
      .filter((b) => b.status === "completed")
      .reduce((s, b) => s + (b.estimated_price || 0), 0),
  };

  if (loading || !user) {
    return <div className="min-h-screen grid place-items-center bg-[#050914] text-white/60">Loading…</div>;
  }

  return (
    <main className="min-h-screen bg-[#050914] text-white" data-testid={ADMIN.dashboard}>
      <header className="border-b border-white/10 px-5 lg:px-8 py-4 flex items-center justify-between sticky top-0 z-30 bg-[#050914]/90 backdrop-blur">
        <Link to="/" className="flex items-center gap-3">
          <Logo size={40} />
          <div className="leading-tight">
            <div className="font-display font-bold text-sm">Authority Detailing Co.</div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-blue-400">Admin Console</div>
          </div>
        </Link>
        <div className="flex items-center gap-3">
          <button
            onClick={fetchAll} disabled={busy}
            className="inline-flex items-center gap-2 text-sm px-3 py-2 rounded-md border border-white/10 hover:bg-white/5"
            data-testid="admin-refresh"
          >
            <RefreshCw className={`h-4 w-4 ${busy ? "animate-spin" : ""}`} /> Refresh
          </button>
          <button
            onClick={logout}
            className="inline-flex items-center gap-2 text-sm px-3 py-2 rounded-md bg-white/5 hover:bg-white/10"
            data-testid={AUTH.logoutBtn}
          >
            <LogOut className="h-4 w-4" /> Logout
          </button>
        </div>
      </header>

      <div className="px-5 lg:px-8 py-8 max-w-7xl mx-auto">
        <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-blue-400 mb-2">Dashboard</div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Bookings</h1>
        <p className="text-white/60 mt-2 text-sm">Manage incoming appointments and customer requests.</p>

        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: "Total", value: stats.total, color: "text-white" },
            { label: "Pending", value: stats.pending, color: "text-yellow-400" },
            { label: "Confirmed", value: stats.confirmed, color: "text-blue-400" },
            { label: "Completed $", value: `$${stats.revenue}`, color: "text-emerald-400" },
          ].map((s) => (
            <div key={s.label} className="rounded-xl bg-[#0B132B] border border-white/10 p-5">
              <div className="text-[11px] uppercase tracking-[0.2em] text-white/50">{s.label}</div>
              <div className={`font-display text-3xl font-bold mt-2 ${s.color}`}>{s.value}</div>
            </div>
          ))}
        </div>

        <div className="mt-8 rounded-xl border border-white/10 overflow-hidden bg-[#0B132B]" data-testid={ADMIN.bookingsTable}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-white/5 text-white/60 text-xs uppercase tracking-wider">
                <tr>
                  <th className="px-4 py-3 text-left">Customer</th>
                  <th className="px-4 py-3 text-left">Package</th>
                  <th className="px-4 py-3 text-left">When</th>
                  <th className="px-4 py-3 text-left">Address</th>
                  <th className="px-4 py-3 text-right">Total</th>
                  <th className="px-4 py-3 text-left">Status</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {bookings.length === 0 && (
                  <tr><td colSpan={7} className="px-4 py-12 text-center text-white/50">No bookings yet.</td></tr>
                )}
                {bookings.map((b) => {
                  const pkg = PACKAGES.find((p) => p.id === b.package);
                  return (
                    <tr key={b.id} className="border-t border-white/5 hover:bg-white/5" data-testid={ADMIN.bookingRow(b.id)}>
                      <td className="px-4 py-3">
                        <div className="font-semibold text-white">{b.name}</div>
                        <div className="text-xs text-white/60 flex items-center gap-1 mt-1"><Phone className="h-3 w-3" />{b.phone}</div>
                        <div className="text-xs text-white/60 flex items-center gap-1"><Mail className="h-3 w-3" />{b.email}</div>
                        {b.photo_url && (
                          <a
                            href={b.photo_url}
                            target="_blank"
                            rel="noreferrer"
                            className="mt-2 inline-flex items-center gap-2 group"
                            data-testid={`admin-photo-link-${b.id}`}
                          >
                            <img
                              src={b.photo_url}
                              alt={`Customer photo from ${b.name}`}
                              className="h-12 w-12 rounded-md object-cover border border-white/10 group-hover:border-blue-500/50 transition-colors"
                            />
                            <span className="text-[10px] uppercase font-bold tracking-wider text-blue-400 group-hover:text-blue-300 inline-flex items-center gap-1">
                              <Camera className="h-3 w-3" /> Photo
                            </span>
                          </a>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {b.booking_type === "bin" ? (
                          <>
                            <div className="text-white">Trash Bin Cleaning</div>
                            <div className="text-xs text-white/50">
                              {b.trash_bins} bin(s) · {BIN_FREQUENCIES.find((f) => f.id === b.frequency)?.label || b.frequency}
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="text-white">{pkg?.label || b.package}</div>
                            <div className="text-xs text-white/50 capitalize">{(b.vehicle_type || "").replace("_", " / ")}</div>
                          </>
                        )}
                        <div className="mt-1 flex flex-wrap gap-1">
                          {b.variable_pricing && (
                            <span className="text-[10px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300">Starting At</span>
                          )}
                          {b.authority_plus_vehicle_optin && (
                            <span className="text-[10px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300 inline-flex items-center gap-0.5">
                              <Crown className="h-2.5 w-2.5" /> Authority+
                            </span>
                          )}
                          {(b.addons?.length || 0) > 0 && (
                            <span className="text-[10px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded bg-white/10 text-white/70">
                              +{b.addons.length} add-on{b.addons.length > 1 ? "s" : ""}
                            </span>
                          )}
                        </div>
                        {(b.addons?.length || 0) > 0 && (
                          <div className="mt-1 text-[11px] text-white/50 leading-tight">
                            {b.addons.map((id) => ADDONS.find((a) => a.id === id)?.name).filter(Boolean).join(" · ")}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-white/80">
                        <div className="flex items-center gap-1"><Calendar className="h-3 w-3 text-blue-400" />{b.preferred_date}</div>
                        <div className="text-xs text-white/50">{b.preferred_time}</div>
                      </td>
                      <td className="px-4 py-3 text-white/70 max-w-[200px]">
                        <div className="flex items-start gap-1 text-xs"><MapPin className="h-3 w-3 mt-0.5 text-blue-400 shrink-0" />{b.address}</div>
                      </td>
                      <td className="px-4 py-3 text-right text-white font-semibold">${b.estimated_price}</td>
                      <td className="px-4 py-3">
                        <Select value={b.status} onValueChange={(v) => updateStatus(b.id, v)}>
                          <SelectTrigger className="bg-white/5 border-white/10 text-white h-9 w-32" data-testid={ADMIN.statusSelect(b.id)}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {STATUSES.map((s) => (
                              <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => remove(b.id)}
                          className="h-9 w-9 grid place-items-center rounded-md text-red-400 hover:bg-red-500/10"
                          data-testid={ADMIN.deleteBooking(b.id)}
                          aria-label="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </main>
  );
}
